import type { XMPPClient } from '@xmppClient';
import type { Chat } from '../../types';
import { PrivacyListAction } from '../../types/constants';
import ChatUtils from './ChatUtils';
import ChatHelpers from './Helpers';

export default class PrivacyList {
  readonly xmlns = 'jabber:iq:privacy';
  public helpers: ChatHelpers;
  public xmppClient: XMPPClient;
  public stanzasCallbacks: Chat.StanzasCallbacks;

  constructor(options: { helpers: ChatHelpers; xmppClient: XMPPClient; stanzasCallbacks: Chat.StanzasCallbacks }) {
    this.helpers = options.helpers;
    this.xmppClient = options.xmppClient;
    this.stanzasCallbacks = options.stanzasCallbacks;
  }

  public async create(list: Chat.PrivacyList): Promise<void> {
    return new Promise((resolve, reject) => {
      const privacyList = list.items.reduce(
        (acc, { user_id, action, mutualBlock }) => {
          acc[user_id] = { action, mutualBlock };
          return acc;
        },
        {} as Record<string, Partial<Chat.PrivacyListItem>>
      );

      const privacyListKeys = Object.keys(privacyList);
      const iqParams = {
        type: 'set',
        from: this.helpers.userCurrentJid,
        id: ChatUtils.getUniqueId('edit'),
      };

      let iq = ChatUtils.createIqStanza(iqParams);

      iq.c('query', { xmlns: this.xmlns }).c('list', { name: list.name });

      for (let index = 0, j = 0, len = privacyListKeys.length; index < len; index++, j = j + 2) {
        const userId = privacyListKeys[index];
        const { action = '', mutualBlock = false } = privacyList[userId];
        const isMutual = mutualBlock && action === 'deny';
        const userJid = this.helpers.jidOrUserId(parseInt(userId, 10));
        const userMuc = this.helpers.getUserNickWithMucDomain(userId);

        this.assignPrivacyItem(iq, { order: j + 1, value: userJid, action, isMutual });
        this.assignPrivacyItem(iq, { order: j + 2, value: userMuc, action, isMutual });
      }

      this.stanzasCallbacks[iqParams.id] = (stanza: Chat.XmlElement) => {
        ChatUtils.isErrorStanza(stanza) ? reject(ChatUtils.buildErrorFromXMPPErrorStanza(stanza)) : resolve();
      };

      this.xmppClient.sendOnline(iq);
    });
  }

  public async getList(name: string): Promise<Chat.PrivacyList> {
    return new Promise((resolve, _reject) => {
      const id = ChatUtils.getUniqueId('getlist');
      const iq = ChatUtils.createIqStanza({
        type: 'get',
        from: this.helpers.userCurrentJid,
        id,
      });

      iq.c('query', { xmlns: this.xmlns }).c('list', { name });

      this.stanzasCallbacks[id] = (stanza: Chat.XmlElement): void => {
        const query = stanza ? ChatUtils.getElement(stanza, 'query') : undefined;
        const list = query ? ChatUtils.getElement(query, 'list') : undefined;

        const privacyList: Chat.PrivacyList = {
          name: ChatUtils.getAttr(list, 'name'),
          items: ChatUtils.getChildElements(list)
            .map((item, index) => {
              if (index % 2 === 0) {
                const { action, value } = item.attrs;
                return {
                  user_id: this.helpers.getUserIdFromJID(value),
                  action: action,
                } as Chat.PrivacyListItem;
              }
              return null;
            })
            .filter((item) => item !== null),
        };

        resolve(privacyList);

        delete this.stanzasCallbacks[id];
      };

      this.xmppClient.sendOnline(iq);
    });
  }

  public async update(list: Chat.PrivacyList): Promise<void> {
    try {
      const currentList = await this.getList(list.name);
      const map = new Map(currentList.items.map((currentListItem) => [currentListItem.user_id, currentListItem]));

      for (const item of list.items) {
        if (item.action === PrivacyListAction.ALLOW) {
          map.delete(item.user_id);
        } else {
          const updatedItem = Object.assign({}, map.get(item.user_id), item);
          map.set(item.user_id, updatedItem);
        }
      }

      const updatedListItems = Array.from(map.values());
      const updatedList = { name: list.name, items: updatedListItems };

      await this.create(updatedList);
    } catch (error) {
      throw error;
    }
  }

  public async getNames(): Promise<Chat.PrivacyListNames> {
    return new Promise((resolve, reject) => {
      const id = ChatUtils.getUniqueId('getNames');
      const iq = ChatUtils.createIqStanza({
        type: 'get',
        from: this.helpers.userCurrentJid,
        id,
      });

      iq.c('query', { xmlns: this.xmlns });

      this.stanzasCallbacks[id] = (stanza: Chat.XmlElement) => {
        if (!ChatUtils.isErrorStanza(stanza)) {
          const query = stanza ? ChatUtils.getElement(stanza, 'query') : undefined;
          const defaultElement = query ? ChatUtils.getElement(query, 'default') : undefined;
          const activeElement = query ? ChatUtils.getElement(query, 'active') : undefined;
          const defaultName = defaultElement ? ChatUtils.getAttr(defaultElement, 'name') : null;
          const activeName = activeElement ? ChatUtils.getAttr(activeElement, 'name') : null;
          const allNames = query
            ? ChatUtils.getChildElements(query).map((listEl) => ChatUtils.getAttr(listEl, 'name'))
            : [];

          const namesList: Chat.PrivacyListNames = {
            default: defaultName,
            active: activeName,
            names: allNames,
          };

          resolve(namesList);
        } else {
          reject(ChatUtils.buildErrorFromXMPPErrorStanza(stanza));
        }
      };

      this.xmppClient.sendOnline(iq);
    });
  }

  public async delete(name: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const id = ChatUtils.getUniqueId('remove');
      const iq = ChatUtils.createIqStanza({
        type: 'set',
        from: this.helpers.userCurrentJid,
        id,
      });

      iq.c('query', { xmlns: this.xmlns }).c('list', { name: name ? name : '' });

      this.stanzasCallbacks[id] = (stanza: Chat.XmlElement) => {
        ChatUtils.isErrorStanza(stanza) ? reject(ChatUtils.buildErrorFromXMPPErrorStanza(stanza)) : resolve();
      };

      this.xmppClient.sendOnline(iq);
    });
  }

  public async setAsDefault(name: string | null): Promise<void> {
    return new Promise((resolve, reject) => {
      const id = ChatUtils.getUniqueId('default');
      const iq = ChatUtils.createIqStanza({
        type: 'set',
        from: this.helpers.userCurrentJid,
        id,
      });

      iq.c('query', { xmlns: this.xmlns }).c('default', name ? { name: name } : {});

      this.stanzasCallbacks[id] = (stanza: Chat.XmlElement) => {
        ChatUtils.isErrorStanza(stanza) ? reject(ChatUtils.buildErrorFromXMPPErrorStanza(stanza)) : resolve();
      };

      this.xmppClient.sendOnline(iq);
    });
  }

  private assignPrivacyItem(iq: Chat.XmlElement, params: Chat.PrivacyListItemParams): void {
    const { value, action, order, isMutual } = params;
    const query = iq ? ChatUtils.getElement(iq, 'query') : undefined;
    const list = query ? ChatUtils.getElement(query, 'list') : undefined;

    if (!list) return;

    const item = list.c('item', { type: 'jid', value, action, order });

    if (isMutual) {
      item.up();

      if (order % 2 === 0) {
        iq.up().up();
      }
    } else {
      item.c('message', {}).up().c('presence-in', {}).up().c('presence-out', {}).up().c('iq', {}).up().up();
    }
  }
}
